import { agenticApiAxios as chatAxios } from "../agentic";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getDefaultOrgId } from "@/utils/defaultOrg";

function readBrowserOrgId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("org_id");
    if (stored) return stored;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("org_id="));
    return cookie ? decodeURIComponent(cookie.slice("org_id=".length)) : null;
  } catch {
    return null;
  }
}

function resolveCurrentOrgId(state: ReturnType<typeof useAuthStoreV1.getState>): string | null {
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const legacyOrgId = readBrowserOrgId();
  if (legacyOrgId) return legacyOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

function chatAuthHeaders(orgId?: string | null, userId?: string | null): Record<string, string> {
  const state = useAuthStoreV1.getState();
  const effectiveOrgId = orgId || resolveCurrentOrgId(state);
  const effectiveUserId = userId || state.user?.id || null;
  const headers: Record<string, string> = {};
  if (effectiveOrgId) headers["X-Organization-ID"] = effectiveOrgId;
  if (effectiveUserId) headers["X-User-ID"] = effectiveUserId;
  return headers;
}

export interface ChatSession {
  id: number;
  uuid: string;
  user_id: string;
  org_id?: string;
  title: string | null;
  status: "active" | "completed" | "archived";
  metadata: Record<string, any>;
  message_count: number;
  messages?: ChatMessage[];
  inserted_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: number;
  uuid: string;
  role: "user" | "assistant";
  content: string;
  execution_type: "direct_llm" | "agent" | "workflow" | null;
  agent_id: number | null;
  workflow_id: number | null;
  workflow_execution_id: number | null;
  input_files: number[];
  metadata: Record<string, any>;
  inserted_at: string;
  updated_at: string;
  agent?: {
    id: number;
    name: string;
    uuid: string;
  };
  workflow?: {
    id: number;
    name: string;
    uuid: string;
  };
}

export interface LLMConfig {
  model?: string;
  provider?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface CreateChatSessionParams {
  user_id: string;
  org_id?: string;
  title?: string;
  metadata?: Record<string, any>;
  session_id?: number;
  user_query?: string;
  file_input?: number[];
  agent_id?: number;
  workflow_id?: number;
  llm_config?: LLMConfig;
  context?: Record<string, any>;
}

export interface FileInput {
  id?: number | string; // Optional: for metadata files with IDs
  name: string;
  content?: string; // Base64 encoded file content for uploaded files
  type?: string; // MIME type (e.g., "image/png", "application/pdf")
  size?: number; // File size in bytes
}

export interface SendMessageParams {
  message: string; // Will be mapped to user_query in payload
  file_input?: FileInput[] | number[]; // Support both new format (objects) and old format (numbers)
  agent_id?: number;
  workflow_id?: number;
  llm_config?: LLMConfig;
  context?: Record<string, any>;
  metadata?: Record<string, any>;
  provider?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  priority?: "low" | "medium" | "high";
  timeout?: number;
}

export interface SendMessageResponse {
  session?: ChatSession;
  user_message?: ChatMessage;
  assistant_message?: ChatMessage;
}

export interface ChatHistoryResponse {
  session: ChatSession;
  messages: ChatMessage[];
}

/**
 * Create a new chat session
 * Supports both flat format (recommended) and nested format
 */
export async function createChatSession(
  params: CreateChatSessionParams
): Promise<ChatSession> {
  // Build payload matching HTML doc format (flat format - recommended)
  const payload: any = {
    user_id: params.user_id,
    ...(params.org_id ? { org_id: params.org_id } : {}),
    ...(params.title ? { title: params.title } : {}),
    ...(params.metadata ? { metadata: params.metadata } : {}),
    ...(params.session_id ? { session_id: params.session_id } : {}),
    ...(params.user_query ? { user_query: params.user_query } : {}),
    ...(params.file_input ? { file_input: params.file_input } : {}),
    ...(params.agent_id ? { agent_id: params.agent_id } : {}),
    ...(params.workflow_id ? { workflow_id: params.workflow_id } : {}),
    ...(params.llm_config ? { llm_config: params.llm_config } : {}),
    ...(params.context ? { context: params.context } : {}),
  };

  // Matching HTML doc: POST /api/chat/sessions
  const response = await chatAxios.post<{ session?: ChatSession; user_message?: ChatMessage; assistant_message?: ChatMessage } | ChatSession>(
    '/chat/sessions',
    payload,
    { headers: chatAuthHeaders(params.org_id, params.user_id) }
  );

  // Handle response structure matching HTML doc:
  // If user_query provided: {data: {session: {...}, user_message: {...}, assistant_message: {...}}}
  // If user_query NOT provided: {data: {id, uuid, ...}} or {data: {session: {...}}}
  const session = (response.data as any)?.session || response.data;
  
  if (!session) {
    console.error('Unexpected response structure:', response.data);
    throw new Error('Invalid response structure from server - no session data found');
  }
  
  // Validate required fields (matching HTML doc validation)
  if (!session.id || !session.uuid) {
    console.error('Session missing required fields:', session);
    throw new Error('Session response missing id or uuid');
  }
  
  return session;
}

/**
 * List chat sessions for a user
 */
export async function listChatSessions(
  userId: string,
  status?: "active" | "completed" | "archived",
  orgId?: string
): Promise<ChatSession[]> {
  const params = new URLSearchParams({ user_id: userId });
  if (status) params.append("status", status);
  if (orgId) params.append("org_id", orgId);

  const response = await chatAxios.get<{ sessions?: ChatSession[] } | ChatSession[]>(
    `/chat/sessions?${params.toString()}`,
    { headers: chatAuthHeaders(orgId, userId) }
  );

  // Handle response structure: {data: {sessions: [...]}, ...} or {data: [...]}
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  return (data as any)?.sessions || [];
}

/**
 * Get a specific chat session with messages
 */
export async function getChatSession(sessionId: string): Promise<ChatSession> {
  const response = await chatAxios.get<{ session?: ChatSession } | ChatSession>(
    `/chat/sessions/${sessionId}`,
    { headers: chatAuthHeaders() }
  );

  // Handle response structure: {data: {session: {...}}, ...} or {data: {...}}
  const session = (response.data as any)?.session || response.data;
  if (!session) {
    throw new Error('Invalid response structure from server - no session data found');
  }
  return session;
}

/**
 * Get chat history for a session
 * Returns session info and all messages in chronological order
 */
export async function getChatHistory(
  sessionId: string
): Promise<ChatHistoryResponse> {
  const response = await chatAxios.get<{ session: ChatSession; messages: ChatMessage[] }>(
    `/chat/sessions/${sessionId}/history`,
    { headers: chatAuthHeaders() }
  );

  // Response structure: {data: {session: {...}, messages: [...]}}
  const data = response.data;
  if (!data) {
    throw new Error('Invalid response structure from server - no data found');
  }
  return {
    session: (data as any).session || data,
    messages: (data as any).messages || [],
  };
}

/**
 * Send a message in a chat session
 * Note: Messages are sent via REST API, but responses come via SSE
 */
export async function sendChatMessage(
  sessionUuid: string,
  params: SendMessageParams
): Promise<SendMessageResponse> {
  const payload: any = {
    user_query: params.message, // Map 'message' to 'user_query' as per API
  };

  // Add optional fields
  if (params.file_input) {
    // Handle both formats: array of objects {id, name} or array of numbers
    if (Array.isArray(params.file_input) && params.file_input.length > 0) {
      if (typeof params.file_input[0] === 'object') {
        // New format: array of objects with id and name
        payload.file_input = params.file_input as FileInput[];
      } else {
        // Old format: array of numbers (for backward compatibility)
        payload.file_input = params.file_input as number[];
      }
    }
  }
  if (params.agent_id) {
    payload.agent_id = params.agent_id;
  }
  if (params.workflow_id) {
    payload.workflow_id = params.workflow_id;
  }
  if (params.llm_config) {
    payload.llm_config = params.llm_config;
  }
  // Add context (e.g., org_id for RAG/Salesforce integration)
  if (params.context) {
    payload.context = params.context;
  }
  if (params.metadata) {
    payload.metadata = params.metadata;
  }
  // Support top-level LLM config fields (alternative to llm_config object)
  if (params.provider) {
    payload.provider = params.provider;
  }
  if (params.model) {
    payload.model = params.model;
  }
  if (params.temperature !== undefined) {
    payload.temperature = params.temperature;
  }
  if (params.max_tokens !== undefined) {
    payload.max_tokens = params.max_tokens;
  }
  if (params.priority) {
    payload.priority = params.priority;
  }
  if (params.timeout !== undefined) {
    payload.timeout = params.timeout;
  }

  const contextOrgId =
    typeof params.context?.org_id === "string" ? params.context.org_id : undefined;
  const response = await chatAxios.post<SendMessageResponse>(
    `/chat/sessions/${sessionUuid}/messages`,
    payload,
    { headers: chatAuthHeaders(contextOrgId) }
  );

  // Response structure: {data: {session: {...}, user_message: {...}, assistant_message: {...}}}
  return response.data;
}

/**
 * Update a chat session (e.g., title, status, metadata)
 * Uses nested format as per documentation: {chat_session: {...}}
 */
export async function updateChatSession(
  sessionId: string,
  updates: { title?: string; status?: "active" | "completed" | "archived"; metadata?: Record<string, any> }
): Promise<ChatSession> {
  const payload = {
    chat_session: {
      ...(updates.title !== undefined ? { title: updates.title } : {}),
      ...(updates.status !== undefined ? { status: updates.status } : {}),
      ...(updates.metadata !== undefined ? { metadata: updates.metadata } : {}),
    },
  };

  const response = await chatAxios.put<{ session?: ChatSession } | ChatSession>(
    `/chat/sessions/${sessionId}`,
    payload,
    { headers: chatAuthHeaders() }
  );

  const session = (response.data as any)?.session || response.data;
  if (!session) {
    throw new Error('Invalid response structure from server - no session data found');
  }
  return session;
}

/**
 * Stop a chat session execution
 * Endpoint: POST /api/chat/sessions/:id/stop
 */
export async function stopChatSession(sessionId: string): Promise<void> {
  await chatAxios.post(`/chat/sessions/${sessionId}/stop`, undefined, {
    headers: chatAuthHeaders(),
  });
}

/**
 * Notify chat that a file was saved in the metadata editor.
 * Triggers test coverage check and impact analysis via SSE events.
 * Endpoint: POST /api/chat/sessions/:id/file_saved
 */
export async function notifyFileSaved(
  sessionUuid: string,
  params: { file_name: string; org_id: string; metadata_type?: string }
): Promise<{ status: string; run_id: string }> {
  const response = await chatAxios.post<{ status: string; run_id: string }>(
    `/chat/sessions/${sessionUuid}/file_saved`,
    params,
    { headers: chatAuthHeaders(params.org_id) }
  );
  return response.data;
}

/**
 * Run tests once for a given test class (no auto-fix loop).
 * Called when user clicks "Run Tests" in the TestCoveragePanel.
 * Endpoint: POST /api/chat/sessions/:id/run_tests
 */
export async function runTests(
  sessionUuid: string,
  params: { test_class: string; class_name: string; org_id: string }
): Promise<{ status: string; run_id: string }> {
  const response = await chatAxios.post<{ status: string; run_id: string }>(
    `/chat/sessions/${sessionUuid}/run_tests`,
    params,
    { headers: chatAuthHeaders(params.org_id) }
  );
  return response.data;
}
